import type { ReactNode } from 'react';
import { COLORS, mono, Card, MetaRow } from '../takeoff/primitives.tsx';
import {
  fmtInt,
  fmtNumber,
  fmtScale,
  type CivilPageBreakdown,
  type CivilTotals,
  type OutputData,
  type RebarLineRow,
} from './helpers.ts';

interface Props {
  data: OutputData;
}

export function OutputPreview({ data }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <CoverCard data={data} />
      <CivilCard data={data} />
      <RebarCard data={data} />
    </div>
  );
}

function CoverCard({ data }: Props) {
  return (
    <Card>
      <div
        style={{
          ...mono(10, COLORS.textDim),
          letterSpacing: '0.1em',
          marginBottom: 8,
        }}
      >
        ESTIMATE OUTPUT
      </div>
      <div
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 22,
          fontWeight: 700,
          marginBottom: 12,
        }}
      >
        {data.submission.job_name}
      </div>
      <MetaRow label="SOURCE PDF" value={data.submission.source_pdf} />
      <MetaRow label="PREPARED BY" value={data.preparedBy} />
      <MetaRow label="GENERATED" value={data.generatedAt.toLocaleString()} />
      <div style={{ height: 1, background: COLORS.border, margin: '12px 0' }} />
      <SummaryGrid data={data} />
    </Card>
  );
}

function SummaryGrid({ data }: Props) {
  const cells: { label: string; value: string; accent?: string }[] = [];
  if (data.civil) {
    cells.push({ label: 'CIVIL LF', value: `${fmtNumber(data.civil.totals.linearLF)}`, accent: COLORS.purple });
    cells.push({ label: 'CIVIL SF', value: `${fmtInt(data.civil.totals.polygonSF)}`, accent: COLORS.purple });
    cells.push({ label: 'CIVIL EA', value: String(data.civil.totals.count), accent: COLORS.purple });
  } else {
    cells.push({ label: 'CIVIL', value: 'no data', accent: COLORS.textDim });
  }
  if (data.rebar) {
    cells.push({ label: 'REBAR LBS', value: fmtInt(data.rebar.totalLbs), accent: COLORS.green });
    cells.push({ label: 'REBAR TONS', value: fmtNumber(data.rebar.totalTons, 2), accent: COLORS.green });
    cells.push({ label: 'REBAR LINES', value: String(data.rebar.lineCount), accent: COLORS.green });
  } else {
    cells.push({ label: 'REBAR', value: 'no data', accent: COLORS.textDim });
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
      {cells.map((c) => (
        <div
          key={c.label}
          style={{
            padding: '10px 12px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 4 }}>
            {c.label}
          </div>
          <div
            style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              color: c.accent ?? COLORS.text,
            }}
          >
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700 }}>{title}</div>
      <div style={mono(11, COLORS.textDim)}>{subtitle}</div>
    </div>
  );
}

function CivilCard({ data }: Props) {
  if (!data.civil) {
    return (
      <Card>
        <SectionHeader title="Civil" subtitle="no data" />
        <div style={{ fontSize: 13, color: COLORS.textDim }}>
          No civil takeoff session for this job.
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <SectionHeader
        title="Civil"
        subtitle={`${data.civil.pages.length} page${data.civil.pages.length === 1 ? '' : 's'} · ${data.civil.totals.itemCount} items`}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {data.civil.pages.map((page) => (
          <CivilPageBlock key={page.pageIndex} page={page} />
        ))}
      </div>
      <div style={{ height: 1, background: COLORS.border, margin: '14px 0 10px' }} />
      <TotalsRow totals={data.civil.totals} />
    </Card>
  );
}

function CivilPageBlock({ page }: { page: CivilPageBreakdown }) {
  return (
    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{page.pageLabel}</div>
        <div style={mono(10, COLORS.textDim)}>scale: {fmtScale(page.scale)}</div>
      </div>
      {page.rows.length === 0 ? (
        <div style={{ ...mono(11, COLORS.textDim) }}>(scale only · no items)</div>
      ) : (
        <Table
          head={['Kind', 'Label', 'Qty', 'Unit']}
          rows={page.rows.map((r) => [
            r.kind,
            r.label,
            r.unit === 'EA' ? String(r.quantity) : fmtNumber(r.quantity),
            r.unit,
          ])}
        />
      )}
    </div>
  );
}

function TotalsRow({ totals }: { totals: CivilTotals }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      <Stat label="LINEAR" value={`${fmtNumber(totals.linearLF)} LF`} />
      <Stat label="AREA" value={`${fmtInt(totals.polygonSF)} SF`} />
      <Stat label="COUNT" value={`${totals.count} EA`} />
    </div>
  );
}

function RebarCard({ data }: Props) {
  if (!data.rebar) {
    return (
      <Card>
        <SectionHeader title="Rebar" subtitle="no data" />
        <div style={{ fontSize: 13, color: COLORS.textDim }}>
          No structural takeoff session for this job.
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <SectionHeader
        title="Rebar"
        subtitle={`${data.rebar.segmentCount} segments · ${data.rebar.lineCount} lines · ${fmtInt(data.rebar.totalLbs)} lb`}
      />
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 6 }}>
          WEIGHT SUMMARY BY SIZE
        </div>
        <Table
          head={['Size', 'Lbs', 'Tons', 'Bars', 'Total LF']}
          rows={data.rebar.weightSummary.map((e) => [
            e.size.toString(),
            fmtInt(e.total),
            fmtNumber(e.tons, 3),
            String(e.barCount),
            fmtNumber(e.totalLinealFeet),
          ])}
          footer={[
            'TOTAL',
            fmtInt(data.rebar.totalLbs),
            fmtNumber(data.rebar.totalTons, 3),
            '',
            '',
          ]}
        />
      </div>
      <div>
        <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 6 }}>
          LINE ITEMS
        </div>
        <RebarLineItemsTable rows={data.rebar.lineRows} />
      </div>
    </Card>
  );
}

function RebarLineItemsTable({ rows }: { rows: RebarLineRow[] }) {
  type Group = { name: string; rows: RebarLineRow[] };
  const groups: Group[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.name === row.segmentName) {
      last.rows.push(row);
    } else {
      groups.push({ name: row.segmentName, rows: [row] });
    }
  }

  const cellSt = { padding: '6px 8px', color: COLORS.textMuted, fontSize: 12 } as const;
  const subSt = {
    padding: '5px 8px',
    fontWeight: 700 as const,
    fontSize: 11,
    background: 'rgba(0,232,122,0.05)',
  };
  const head = ['Segment', 'Line', 'Mat', 'Fn', 'Size', 'Cls', 'Qty', 'Mult', 'Total LF', 'Wt (lb)'];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  padding: '6px 8px',
                  borderBottom: `1px solid ${COLORS.border}`,
                  ...mono(10, COLORS.textDim),
                  letterSpacing: '0.08em',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((group) => {
            const subtotalLF = group.rows.reduce((sum, r) => sum + r.totalLengthFt, 0);
            const subtotalLbs = group.rows.reduce((sum, r) => sum + r.totalWeightLbs, 0);
            return [
              ...group.rows.map((r, ri) => (
                <tr key={`${group.name}-${ri}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={cellSt}>{r.segmentName}</td>
                  <td style={cellSt}>{r.lineNumber}</td>
                  <td style={cellSt}>{r.material}</td>
                  <td style={cellSt}>{r.function}</td>
                  <td style={cellSt}>{r.size}</td>
                  <td style={cellSt}>{r.class}</td>
                  <td style={cellSt}>{r.quantity}</td>
                  <td style={cellSt}>{r.multiplier}</td>
                  <td style={cellSt}>{fmtNumber(r.totalLengthFt, 2)}</td>
                  <td style={cellSt}>{fmtInt(r.totalWeightLbs)}</td>
                </tr>
              )),
              <tr key={`${group.name}-sub`} style={{ borderBottom: `2px solid ${COLORS.borderStrong}` }}>
                <td colSpan={8} style={{ ...subSt, color: COLORS.textDim, textAlign: 'right' }}>
                  {group.name}
                </td>
                <td style={{ ...subSt, color: COLORS.green }}>{fmtNumber(subtotalLF, 2)}</td>
                <td style={{ ...subSt, color: COLORS.green }}>{fmtInt(subtotalLbs)}</td>
              </tr>,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
      }}
    >
      <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700 }}>
        {value}
      </div>
    </div>
  );
}

function Table({
  head,
  rows,
  footer,
}: {
  head: string[];
  rows: ReactNode[][];
  footer?: ReactNode[];
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
        }}
      >
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  padding: '6px 8px',
                  borderBottom: `1px solid ${COLORS.border}`,
                  ...mono(10, COLORS.textDim),
                  letterSpacing: '0.08em',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{ padding: '6px 8px', color: COLORS.textMuted, fontSize: 12 }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              {footer.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: '8px',
                    fontWeight: 700,
                    color: COLORS.green,
                    borderTop: `1px solid ${COLORS.border}`,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
